/**
 * SLAM Sidebar Shared Styles — UNC Carolina Athletics v3.0
 * =====================================================
 * Unified style utilities for all sidebar implementations:
 *   - Sidebar.js (legacy main sidebar)
 *   - UnifiedSidebar.jsx (basketball/baseball sport sidebars)
 *   - ui/sidebar.js (shadcn/Radix sidebar primitive)
 *
 * Usage:
 *   import { sidebarStyles, getSidebarTheme } from '../tokens';
 *
 *   const theme = getSidebarTheme();
 *   <div style={{ background: theme.background }}>
 *
 *   const classes = sidebarStyles.container(isCollapsed);
 */

const { sidebar } = require('./design-tokens');

// =============================================================================
// THEME GETTER — OLED only (no light mode)
// =============================================================================

/**
 * Returns the sidebar color tokens. Always returns dark/OLED theme.
 * @param {boolean} _isDarkMode - Ignored (kept for backward compat)
 * @returns {Object} OLED sidebar color tokens
 */
function getSidebarTheme(_isDarkMode) {
  return sidebar.dark;
}

// =============================================================================
// TAILWIND CLASS BUILDERS — OLED Edition
// =============================================================================

const sidebarStyles = {

  /**
   * Sidebar root container classes
   * TDD: 200px expanded, 56px collapsed
   */
  container: (_isDarkMode, isCollapsed = false, isOpen = true) => {
    const width = isCollapsed ? 'w-[56px]' : 'w-[200px]';
    const base = `${width} h-full flex flex-col transition-all duration-300 ease-in-out border-r`;
    const theme = 'bg-black text-white border-[#1A1A1A]';
    const mobile = isOpen
      ? 'translate-x-0'
      : '-translate-x-full md:translate-x-0';
    return `${base} ${theme} ${mobile}`;
  },

  /**
   * Section header classes (e.g., "GENERAL MANAGER")
   * TDD: 9px, uppercase, 2px letter-spacing, --text-quaternary
   */
  sectionHeader: (_isDarkMode) => {
    return 'flex items-center justify-between px-4 py-2 cursor-pointer transition-colors duration-200 uppercase font-semibold text-[9px] tracking-[2px] text-[#777777] hover:text-[#AAAAAA]';
  },

  /**
   * Navigation item classes — Carolina Blue active with 2px left border
   */
  navItem: (_isDarkMode, isActive = false, isCollapsed = false, isHighlight = false) => {
    const layout = isCollapsed
      ? 'justify-center px-2 mx-1 py-2.5'
      : 'px-3 mx-1 py-2 gap-3';

    let colorClasses;
    if (isActive) {
      colorClasses = 'bg-[rgba(123,175,212,0.15)] text-[#7BAFD4] border-l-2 border-[#7BAFD4]';
    } else if (isHighlight) {
      colorClasses = 'bg-[rgba(123,175,212,0.08)] text-[#7BAFD4] hover:bg-[rgba(123,175,212,0.12)] border border-[rgba(123,175,212,0.2)]';
    } else {
      colorClasses = 'text-[#999999] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F5F5F5]';
    }

    return `w-auto flex items-center relative ${layout} ${colorClasses} transition-all duration-200 rounded-[10px] group`;
  },

  /**
   * Navigation item classes for UnifiedSidebar (border-left active style)
   */
  navItemUnified: (_isDarkMode, isActive = false, isOpen = true) => {
    const layout = isOpen ? 'px-3 gap-3' : 'justify-center px-2';

    let colorClasses;
    if (isActive) {
      colorClasses = 'bg-[rgba(123,175,212,0.15)] text-[#7BAFD4] border-l-2 border-[#7BAFD4]';
    } else {
      colorClasses = 'text-[#999999] hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F5F5F5]';
    }

    return `w-full py-2 flex items-center transition-all ${layout} ${colorClasses}`;
  },

  /**
   * Icon wrapper classes
   */
  icon: (_isDarkMode, isActive = false) => {
    if (isActive) return 'text-[#7BAFD4]';
    return 'text-[#999999] group-hover:text-[#F5F5F5]';
  },

  /**
   * Icon wrapper for UnifiedSidebar active state
   */
  iconUnified: (isActive = false) => {
    return `flex-shrink-0 ${isActive ? 'text-[#7BAFD4]' : ''}`;
  },

  /**
   * Footer area classes
   */
  footer: (_isDarkMode, isCollapsed = false) => {
    const width = isCollapsed ? 'w-[56px]' : 'w-[200px]';
    const base = `${width} px-3 py-2 transition-all duration-300 border-t`;
    return `${base} border-[#1A1A1A]`;
  },

  /**
   * Action/toggle button classes
   */
  actionButton: (_isDarkMode) => {
    return 'p-2.5 rounded-[10px] transition-all duration-200 hover:bg-[rgba(255,255,255,0.04)] text-[#999999] hover:text-[#F5F5F5]';
  },

  /**
   * Chevron icon rotation class
   */
  chevron: (isExpanded) => {
    return `transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`;
  },

  /**
   * Badge classes (notification count)
   */
  badge: (_isDarkMode, isActive = false) => {
    return 'bg-red-500/80 text-white';
  },

  /**
   * Collapsed badge (small circle on icon)
   */
  badgeCollapsed: () => {
    return 'absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center text-[10px] font-bold rounded-full bg-red-500 text-white animate-pulse';
  },
};

module.exports = { sidebarStyles, getSidebarTheme };
